import TextFontUpdater from 'client/components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import Input from 'client/components/core/Sidebar/components/Settings/components/Input/Input'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import AlignStyle from 'client/components/core/Sidebar/components/Settings/styles/AlignStyle'
import FontSize from 'client/components/core/Sidebar/components/Settings/styles/FontSize'
import LineHeight from 'client/components/core/Sidebar/components/Settings/styles/LineHeight'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import { pageSelectors, usePage } from 'client/store'
import { blogPageTypes } from 'common/constants/pageTypes'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import { CheckboxInterface } from 'common/types/entities/CheckboxInterface'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { OptionWrapper } from 'src/editor/layout/sidebar/settings/option-wrapper'
import { pagesWithAutomationRulesTypes } from 'src/editor/shared/constants/automation-rules'
import { AutomationRulesSettings } from 'src/editor/shared/settings/automation-rules-settings'
import { CheckboxControl } from 'src/editor/shared/settings/checkbox-control'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { AutomationRuleTriggersEnum } from 'src/editor/shared/types/automation-rules/triggers-types'

export interface CheckboxSettingsProps {
  entity: CheckboxInterface
}

const CheckboxSettings = ({ entity }: CheckboxSettingsProps) => {
  const updateProp = useUpdateProp(entity)
  const { isMobile } = useDeviceModeContext()
  const updateEntity = useUpdateEntity<CheckboxInterface>()

  const pageType = usePage(pageSelectors.getPageType)
  const { isTemplate } = usePageRouteDetector()
  const isLoginPage =
    pageType === PageTypeEnum.CustomLogin ||
    pageType === PageTypeEnum.WebsiteLogin

  const isAutomationRulesVisible =
    !isTemplate && pagesWithAutomationRulesTypes.includes(pageType)
  const automationTriggerType = blogPageTypes.includes(pageType)
    ? AutomationRuleTriggersEnum.blog_page_form_subscribed
    : AutomationRuleTriggersEnum.form_subscribed

  return (
    <React.Fragment>
      <Input
        update={updateProp('errorText')}
        value={entity.errorText}
        label="settings_options.checkbox_message"
        disabled={entity.optional}
      />
      {!isLoginPage && (
        <CheckboxControl
          label="entity_settings.explicit_consent.optional"
          tooltipText="entity_settings.explicit_consent.optional_popover"
          value={entity.optional}
          update={updateProp('optional')}
        />
      )}
      {isAutomationRulesVisible && (
        <OptionWrapper
          labelText="entity_settings.automation_rule.automations"
          className="static"
        >
          <AutomationRulesSettings
            entityId={entity.id}
            triggerKey={'checkBoxId'}
            triggerType={automationTriggerType}
          />
        </OptionWrapper>
      )}
      <GroupTitle>typography</GroupTitle>
      <FontSize
        update={updateProp('fontSize')}
        mobileUpdate={updateProp('mobileFontSize')}
        fontSize={entity.fontSize}
        mobileFontSize={entity.mobileFontSize}
      />
      <LineHeight
        lineHeight={entity.lineHeight}
        mobileLineHeight={entity.mobileLineHeight}
        update={updateProp('lineHeight')}
        mobileUpdate={updateProp('mobileLineHeight')}
        fontSize={entity.fontSize}
        mobileFontSize={entity.mobileFontSize}
      />
      <TextFontUpdater<CheckboxInterface>
        entity={entity}
        update={updateEntity}
      />
      <AlignStyle
        align={isMobile ? entity.mobileTextAlign : entity.textAlign}
        update={
          isMobile ? updateProp('mobileTextAlign') : updateProp('textAlign')
        }
        isFlex={false}
      />
      <GroupTitle>color</GroupTitle>
      <ColorPickerControl
        update={updateProp('color')}
        mobileUpdate={updateProp('mobileColor')}
        color={entity.color}
        mobileColor={entity.mobileColor}
        label="text color"
      />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </React.Fragment>
  )
}

export default CheckboxSettings
